import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LogoMark } from "./LogoMark";

const LINKS = [
  { id: "nav-link-use-cases", label: "Use cases", href: "#use-cases" },
  { id: "nav-link-collections", label: "Collections", href: "#collections" },
  { id: "nav-link-how-it-works", label: "How it works", href: "#how-it-works" },
  { id: "nav-link-security", label: "Security", href: "#security" },
  { id: "nav-link-faq", label: "FAQ", href: "#faq" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled
            ? "bg-[#FAF9F6]/80 backdrop-blur-xl border-b border-zinc-200/70"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-[72px] flex items-center justify-between">
          <a
            data-testid="nav-brand-logo"
            href="#top"
            onClick={(e) => { e.preventDefault(); go("#top"); }}
            className="flex items-center gap-2.5 group"
          >
            <LogoMark size={30} className="transition-transform duration-500 group-hover:rotate-6" />
            <span className="font-display font-800 text-lg tracking-tight" style={{ fontWeight: 800 }}>
              Voicxent
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a
                key={l.id}
                data-testid={l.id}
                href={l.href}
                onClick={(e) => { e.preventDefault(); go(l.href); }}
                className="text-sm text-zinc-600 hover:text-zinc-950 transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#2563EB] after:transition-[width] after:duration-300 hover:after:w-full"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-contact-button"
              onClick={() => go("#contact")}
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#09090B] text-white text-sm font-medium pl-4 pr-3 py-2 rounded-full hover:bg-[#2563EB] transition-colors duration-300"
            >
              Book a demo
              <ArrowUpRight size={15} />
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 -mr-2 text-zinc-900"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#FAF9F6]/95 backdrop-blur-xl lg:hidden pt-24 px-6"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.href}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                  onClick={(e) => { e.preventDefault(); go(l.href); }}
                  className="font-display text-3xl font-bold py-3 border-b border-zinc-200 text-zinc-900"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.36, duration: 0.4 }}
                data-testid="nav-mobile-contact-button"
                onClick={() => go("#contact")}
                className="mt-6 inline-flex items-center justify-center gap-2 bg-[#09090B] text-white font-medium px-6 py-3.5 rounded-full"
              >
                Book a demo <ArrowUpRight size={16} />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
